import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CookieConsentService } from './cookie-consent.service';
import { ExternalScriptLoaderService } from './external-script-loader.service';

export type AnalyticsEvent =
  | 'signup_started'
  | 'signup_completed'
  | 'login_completed'
  | 'login_failed'
  | 'logout'
  | 'religion_picked'
  | 'religion_changed'
  | 'address_created'
  | 'ask_submitted'
  | 'ask_answered'
  | 'ask_payment_required'
  | 'ask_invalid_question'
  | 'ask_no_answer_found'
  | 'ask_daily_limit_reached'
  | 'reflection_rated'
  | 'daily_verse_shared'
  | 'daily_verse_text_copied'
  | 'daily_verse_saved'
  | 'pack_purchased'
  | 'single_ask_purchased'
  | 'pix_generated'
  | 'pix_confirmed'
  | 'streak_milestone_reached'
  | 'streak_bonus_claimed'
  | 'memory_opened'
  | 'memory_dismissed'
  | 'subscription_started'
  | 'subscription_canceled'
  | 'onboarding_completed'
  | 'onboarding_skipped'
  | 'account_deletion_started'
  | 'account_deletion_confirmed'
  | 'account_deletion_canceled'
  | 'no_address_modal_dismissed'
  | 'tab_changed'
  | 'auth_google_attempt'
  | 'auth_google_success'
  | 'auth_google_failure'
  | 'auth_google_button_clicked'
  | 'auth_google_no_response'
  | 'profile_completed';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private initialized = false;
  private scheduled = false;
  private initPromise?: Promise<void>;
  private posthog?: any;
  private queue: Array<() => void> = [];

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private consent: CookieConsentService,
    private scripts: ExternalScriptLoaderService,
  ) {}

  initialize(): void {
    if (this.initialized || this.scheduled) return;

    if (!this.hasConfiguredAnalytics()) return;
    if (typeof window === 'undefined') return;

    this.scheduled = true;
    this.scheduleAfterFirstPaint(() => void this.load());
  }

  private async load(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      await Promise.all([this.loadGoogleAnalytics(), this.loadPosthog()]);
      this.initialized = true;
      this.flushQueue();
    })();

    return this.initPromise;
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    const safeTraits = this.sanitize(traits);
    this.enqueueOrRun(() => this.posthog?.identify(userId, safeTraits));
  }

  track(event: AnalyticsEvent, properties?: Record<string, unknown>): void {
    const safeProps = this.sanitize(properties);
    this.enqueueOrRun(() => {
      this.posthog?.capture(event, safeProps);
      this.sendGoogleEvent(event, safeProps);
    });
  }

  reset(): void {
    this.enqueueOrRun(() => this.posthog?.reset());
  }

  private async loadGoogleAnalytics(): Promise<void> {
    const id = (environment as any).GOOGLE_ANALYTICS_ID;
    if (!this.isEnabled(id)) return;

    const windowRef = window as any;
    windowRef.dataLayer = windowRef.dataLayer || [];
    windowRef.gtag =
      windowRef.gtag ||
      function gtag() {
        // eslint-disable-next-line prefer-rest-params
        windowRef.dataLayer.push(arguments);
      };

    windowRef.gtag('consent', 'default', {
      analytics_storage: this.consent.allowsTracking ? 'granted' : 'denied',
    });

    await this.scripts.load({
      id: 'google-analytics-gtag',
      src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`,
      async: true,
    });

    windowRef.gtag('js', new Date());
    windowRef.gtag('config', id, {
      page_title: this.document.title,
      page_location: window.location.href,
    });
  }

  private async loadPosthog(): Promise<void> {
    const apiKey = (environment as any).POSTHOG_API_KEY;
    if (!this.isEnabled(apiKey)) return;
    if (!this.consent.allowsTracking) return;

    const module = await import('posthog-js');
    const posthog = module.default;
    const apiHost = (environment as any).POSTHOG_HOST || 'https://us.posthog.com';

    posthog.init(apiKey, {
      api_host: apiHost,
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      disable_session_recording: true,
      autocapture: false,
      respect_dnt: true,
      loaded: (instance) => {
        instance.register({
          environment: environment.ENVIRONMENT,
          app_version: (environment as any).APP_VERSION || 'unknown',
        });
      },
    });

    this.posthog = posthog;
  }

  private enqueueOrRun(task: () => void): void {
    if (!this.hasConfiguredAnalytics()) return;

    this.initialize();

    if (this.initialized) {
      task();
      return;
    }

    this.queue.push(task);
  }

  private flushQueue(): void {
    const queue = this.queue.splice(0);
    queue.forEach((task) => task());
  }

  private sendGoogleEvent(
    event: AnalyticsEvent,
    properties?: Record<string, unknown>,
  ): void {
    const gtag = (window as any)?.gtag;
    if (typeof gtag !== 'function') return;
    gtag('event', event, properties ?? {});
  }

  private scheduleAfterFirstPaint(task: () => void): void {
    const run = () => {
      let done = false;
      const execute = () => {
        if (done) return;
        done = true;
        task();
      };

      window.setTimeout(execute, 30000);
      window.addEventListener('pointerdown', execute, { passive: true, once: true });
      window.addEventListener('keydown', execute, { once: true });
    };

    if (document.readyState === 'complete') {
      run();
      return;
    }

    window.addEventListener('load', run, { once: true });
  }

  private isEnabled(value: unknown): value is string {
    return typeof value === 'string' && value.trim() !== '' && value !== 'OFF';
  }

  private hasConfiguredAnalytics(): boolean {
    return (
      this.isEnabled((environment as any).POSTHOG_API_KEY) ||
      this.isEnabled((environment as any).GOOGLE_ANALYTICS_ID)
    );
  }

  private sanitize(input?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!input) return undefined;
    const blocked = new Set([
      'password',
      'token',
      'refresh_token',
      'access_token',
      'biometry_assertion',
      'card_token',
      'cvv',
      'document',
      'cpf',
      'rg',
      'pix_qr_code',
      'pix_qr_code_base64',
    ]);
    const safe: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(input)) {
      if (blocked.has(key.toLowerCase())) continue;
      safe[key] = value;
    }
    return safe;
  }
}
